import { useLayoutEffect, useState } from 'react';
import { SingleUnit } from './equipmentTypes';
import { Btn } from '../ui';
import {
    addReplacedCounterReading,
    applyReplacedCounterEntries,
    parseReplacedCounterHistory,
} from './equipmentFieldUtils';
import { UnitRecord } from './equipmentTypes';

export const ReplacedCounterEditor = ({
    label,
    unit,
    formData,
    setFormData,
}: {
    label: string;
    unit: SingleUnit;
    formData: Partial<SingleUnit>;
    setFormData: (data: Partial<SingleUnit>) => void;
}) => {
    const [addValue, setAddValue] = useState('');
    const [addError, setAddError] = useState<string | null>(null);

    const historyJson =
        (formData as UnitRecord).replacedUMCounterHistory ??
        (unit as UnitRecord).replacedUMCounterHistory;
    const historyEntries = parseReplacedCounterHistory(
        typeof historyJson === 'string' ? historyJson : null,
    );

    const historySum = historyEntries.reduce((sum, entry) => sum + entry.value, 0);
    const storedTotal =
        formData.replacedUMCounterValue !== undefined
            ? Number(formData.replacedUMCounterValue) || 0
            : unit.replacedUMCounterValue ?? 0;

    // History readings are the source of truth for the cumulative total.
    const currentTotal = historyEntries.length > 0 ? historySum : storedTotal;
    const totalBreakdown =
        historyEntries.length > 1
            ? ` (${historyEntries.map((entry) => entry.value.toLocaleString()).join(' + ')})`
            : '';

    // Keep stored total aligned with history sum while editing.
    useLayoutEffect(() => {
        if (historyEntries.length === 0) return;
        if (Math.abs(historySum - storedTotal) < 1e-9) return;
        setFormData(applyReplacedCounterEntries(formData, historyEntries));
        // eslint-disable-next-line react-hooks/exhaustive-deps -- sync only when sum/stored diverge
    }, [historySum, storedTotal, historyEntries.length]);

    const addReading = () => {
        const parsed = Number(addValue);
        if (addValue.trim() === '' || !Number.isFinite(parsed) || parsed < 0) {
            setAddError('Enter 0 or greater to add onto the cumulative total.');
            return;
        }
        setAddError(null);
        setFormData(addReplacedCounterReading(formData, unit as UnitRecord, parsed));
        setAddValue('');
    };

    return (
        <div className="info-row editing replaced-counter-editor">
            <span className="info-label">{label}</span>
            <div className="info-value-edit replaced-counter-content">
                <p className="equipment-field-hint" id="replaced-counter-total">
                    Current cumulative total:{' '}
                    <strong>
                        {currentTotal.toLocaleString()}
                        {totalBreakdown}
                    </strong>
                </p>
                {historyEntries.length > 0 && (
                    <p className="equipment-field-hint">
                        History:{' '}
                        {historyEntries
                            .map((entry) => {
                                const value = entry.value.toLocaleString();
                                return entry.date ? `${value} (${entry.date})` : value;
                            })
                            .join('; ')}
                    </p>
                )}
                <p className="equipment-field-hint" id="replaced-counter-hint">
                    Enter the hours on the replaced counter. That reading is added to the cumulative
                    total (utilization, PM scheduling, etc.). Enter 0 or greater.
                </p>
                <div className="replaced-counter-add">
                    <input
                        type="number"
                        step="any"
                        min={0}
                        className="replaced-counter-input"
                        placeholder="Hours on replaced counter"
                        value={addValue}
                        onChange={(event) => {
                            setAddValue(event.target.value);
                            if (addError) setAddError(null);
                        }}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                                event.preventDefault();
                                addReading();
                            }
                        }}
                        aria-label={`Add ${label} reading`}
                        aria-describedby="replaced-counter-hint replaced-counter-total"
                    />
                    <Btn
                        variant="secondary"
                        buttonText="Add to total"
                        buttonOnClick={addReading}
                        buttonDisabled={
                            addValue.trim() === '' ||
                            !Number.isFinite(Number(addValue)) ||
                            Number(addValue) < 0
                        }
                        buttonClasses="replaced-counter-add-btn"
                    />
                </div>
                {addError && (
                    <p className="field-inline-error" role="alert">
                        {addError}
                    </p>
                )}
            </div>
        </div>
    );
};
